import React, { ReactNode, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { IconButton } from '@components/iconButton';
import { SnackbarContent, SnackbarProps, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ExclamationTriangleSolid, CheckCircleSolid, ExclamationCircleSolid, InfoCircle, Times } from '@assets/svgs';
import { ToastType } from '@components/toast/ToastType.enum';
import { getOrCreateSessionId } from '@utils/sessionId';

export type ToastBaseProps = {
  message: string;
  variant: ToastType;
  showSessionId?: boolean;
};

export type ToastProps = SnackbarProps &
  ToastBaseProps & {
    noAutoHide?: boolean;
  };

interface IIconTypes {
  [key: string]: ReactNode;
}

const iconTypes: IIconTypes = {
  [ToastType.Warning]: <ExclamationTriangleSolid />,
  [ToastType.Error]: <ExclamationCircleSolid />,
  [ToastType.Success]: <CheckCircleSolid />,
  [ToastType.Info]: <InfoCircle />,
  // [ToastType.SiteAlert]: <Refresh />,
};

// TODO styles should come from theme

const StyledSnackbar = styled(SnackbarContent, {
  name: 'Toast',
  slot: 'Root',
})<SnackbarProps>(({ theme }) => ({
  '.SnackbarContainer-root': {
    zIndex: 100000000000,
  },
  borderRadius: theme.shape.borderRadius * 2,
  backgroundColor: theme.palette.primary.dark,
  borderLeft: '6px solid',
  justifyContent: 'space-between',
  maxWidth: '400px',
  minHeight: '56px',
  flexWrap: 'nowrap',
  '.MuiSnackbarContent-message': {
    color: theme.palette.common.white,
    display: 'flex',
    maxWidth: '350px',
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  '& .MuiSnackbarContent-action .MuiButton-root': {
    color: 'white',
    '&:hover': {
      textDecoration: 'none',
    },
  },
  '&.success': {
    borderLeft: `6px solid ${theme.palette.success.main}`,
    color: theme.palette.success.main,
  },
  '.success': {
    color: theme.palette.success.main,
    svg: {
      fill: theme.palette.success.main,
    },
  },
  '&.error': {
    borderLeft: `6px solid ${theme.palette.error.main}`,
  },
  '.error': {
    color: theme.palette.error.main,
  },
  '&.warning': {
    borderLeft: `6px solid ${theme.palette.warning.main}`,
  },
  '.warning': {
    color: theme.palette.warning.main,
  },
  '&.info': {
    borderLeft: `6px solid ${theme.palette.info.main}`,
  },
  '.info': {
    color: theme.palette.info.main,
  },

  '&.siteUpdateAlert': {
    backgroundColor: theme.palette.button.tertiaryDark,
    borderLeft: 'none',
    maxWidth: 'unset',

    '.MuiSnackbarContent-message': {
      maxWidth: '380px',
      whiteSpace: 'break-spaces',
    },
  },

  // remove overlay which is making the item lighter
  '--Paper-overlay': 'unset !important',
}));

const ToastMessage = ({ message, variant, showSessionId = false }: ToastBaseProps) => {
  const icon = iconTypes[variant];

  const isSiteAlert = variant === ToastType.SiteUpdateAlert;
  const sessionId = showSessionId ? getOrCreateSessionId() : null;

  return (
    <>
      {icon && (
        <IconButton sx={{ margin: '0 8px 0 8px', cursor: 'auto', height: 'auto' }} className={variant} disableRipple>
          {icon}
        </IconButton>
      )}
      <div
        style={{
          minHeight: '36px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          maxWidth: isSiteAlert ? 'unset' : '300px',
          paddingRight: '16px',
        }}
      >
        <Typography
          variant='bodySmall'
          sx={{
            color: 'common.white',
          }}
        >
          {message}
        </Typography>
        {sessionId && (
          <Typography
            variant='bodySmall'
            sx={{
              color: 'rgba(255,255,255,0.5)',
              fontSize: '10px',
              mt: '2px',
              wordBreak: 'break-all',
            }}
          >
            Session: {sessionId}
          </Typography>
        )}
      </div>
    </>
  );
};

export const Toast = React.forwardRef<HTMLDivElement, ToastProps>(
  ({ message, variant, id, showSessionId, ...other }, ref) => {
    const { closeSnackbar } = useSnackbar();

    const handleDismiss = useCallback(() => {
      closeSnackbar(id);
    }, [id, closeSnackbar]);

    const ActionButtons = other.action ?? (
      <IconButton
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          svg: { color: (theme) => (theme.palette.theme === 'lio' ? 'common.white' : theme.palette.text.main) },
        }}
        variant='circularDark'
        size='small'
        onClick={handleDismiss}
      >
        <Times width='10px' height='10px' />
      </IconButton>
    );

    return (
      <StyledSnackbar
        ref={ref}
        className={variant}
        message={<ToastMessage message={message || ''} variant={variant} showSessionId={showSessionId} />}
        action={ActionButtons}
      />
    );
  },
);

Toast.displayName = 'Toast';
